import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userThemeSelector } from '../../../../store/user/user.selectors';
import { setUserTheme } from '../../../../store/user/user.actions';
import LytIcon from '../../../library/icon/LytIcon';
import LytToggle from '../../../library/toggle/LytToggle';
import LytTooltip from '../../../library/tooltip/LytTooltip';
import PropTypes from 'prop-types';
import { LEGACY_THEME_TYPES } from '../../../styled/theme/ThemeProvider';
import { ICON_NAMES } from '../../../library/icon/icon-constants';
import styled from 'styled-components';
const ThemeSelectorContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: ${({ $collapsed }) => ($collapsed ? 'column' : 'row')};
    gap: 8px;
    padding: 8px 16px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    ${({ $collapsed }) =>
        $collapsed &&
        `
        justify-content: center;
        padding: 8px 0;
        > div > div {
            justify-content: center;
        }
    `}
`;
const ToggleContainer = styled.div`
    width: 100%;
`;

/**
 * @typedef {Object} ThemeSelectorProps
 * @property {boolean} collapsed - Whether the theme selector is collapsed
 */

/**
 * Theme selector component that uses a moon icon and toggle to switch between light and dark themes
 * @param {ThemeSelectorProps} props
 * @returns {React.ReactNode}
 */
const LytThemeSelector = ({ collapsed = false }) => {
    const theme = useSelector(userThemeSelector);
    const dispatch = useDispatch();
    const handleThemeToggle = useCallback(
        (isDarkMode) => {
            const newTheme = isDarkMode
                ? LEGACY_THEME_TYPES.dark
                : LEGACY_THEME_TYPES.light;
            if (newTheme !== theme) {
                dispatch(setUserTheme(newTheme));
            }
        },
        [dispatch, theme]
    );
    const isDarkMode = theme === LEGACY_THEME_TYPES.dark;

    // When collapsed, show just the icon that can be clicked to toggle
    if (collapsed) {
        return (
            <LytTooltip
                content={
                    isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
                }
                position="right">
                <ThemeSelectorContainer
                    $collapsed={collapsed}
                    onClick={() => handleThemeToggle(!isDarkMode)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleThemeToggle(!isDarkMode);
                        }
                    }}
                    aria-label={
                        isDarkMode
                            ? 'Switch to light mode'
                            : 'Switch to dark mode'
                    }>
                    <LytIcon
                        name={
                            isDarkMode
                                ? ICON_NAMES.MoonFilled
                                : ICON_NAMES.SunFilled
                        }
                        aria-hidden="true"
                    />
                </ThemeSelectorContainer>
            </LytTooltip>
        );
    }
    return (
        <ThemeSelectorContainer
            $collapsed={collapsed}
            onClick={() => handleThemeToggle(!isDarkMode)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleThemeToggle(!isDarkMode);
                }
            }}
            aria-label="Toggle dark mode">
            <LytIcon name={ICON_NAMES.MoonFilled} aria-hidden="true" />
            <ToggleContainer onClick={(e) => e.stopPropagation()}>
                <LytToggle
                    label="Dark Mode"
                    labelPosition="left"
                    checked={isDarkMode}
                    onChange={handleThemeToggle}
                    ariaLabel="Toggle dark mode"
                    testId="theme-toggle"
                />
            </ToggleContainer>
        </ThemeSelectorContainer>
    );
};
LytThemeSelector.propTypes = {
    collapsed: PropTypes.bool,
};
export default LytThemeSelector;
